import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import { useCart } from '../contexts/CartContext';
import {
  ArrowRight,
  Check,
  Minus,
  Plus,
  Settings,
  Star,
  X,
} from 'lucide-react';
import { getModulesByCategory, getPackages } from '../services/api';
import PriceSkeleton from './PriceSkeleton';
import {
  calculateCustomTotal,
  createFallbackModuleGroups,
  createFallbackPackages,
  expandModuleIds,
  getCategoryContent,
  getModuleContent,
  getPackageContent,
  getPricingCopy,
  updateModuleQuantity,
} from '../data/pricing';

const STACKED_PRICE_CODES = new Set(['AED', 'KWD']);
const LONG_INLINE_PRICE_CODES = new Set(['TRY']);

const PackagePriceText = ({ amount, formatPrice, pricingRegion, variant = 'large' }) => {
  const currencyCode = pricingRegion?.currencyCode;

  if (STACKED_PRICE_CODES.has(currencyCode)) {
    const formattedAmount = new Intl.NumberFormat(pricingRegion?.locale || 'en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));
    const amountSize =
      variant === 'compact' ? 'text-3xl md:text-4xl' : 'text-5xl md:text-[56px]';
    const codeSize = variant === 'compact' ? 'text-lg md:text-xl' : 'text-2xl md:text-3xl';

    return (
      <span className="price-value flex min-w-0 max-w-full flex-col items-start text-[#00FFD1]">
        <span className={`${codeSize} font-bold leading-none`}>
          {currencyCode}
        </span>
        <span className={`${amountSize} font-bold leading-[0.95]`}>
          {formattedAmount}
        </span>
      </span>
    );
  }

  const formattedPrice = formatPrice(amount);
  const isLongInlinePrice =
    LONG_INLINE_PRICE_CODES.has(currencyCode) ||
    formattedPrice.replace(/\s/g, '').length >= 8;
  const amountSize = variant === 'compact'
    ? isLongInlinePrice ? 'text-[30px] md:text-3xl' : 'text-3xl'
    : isLongInlinePrice ? 'text-[42px] md:text-[46px] lg:text-[48px]' : 'text-5xl md:text-6xl';

  return (
    <span className={`price-value price-value-inline block max-w-full text-[#00FFD1] ${amountSize} font-bold leading-[0.95]`}>
      {formattedPrice}
    </span>
  );
};

const Pricing = ({ headingLevel = 'h2' }) => {
  const { isRTL, currentLang } = useLanguage();
  const {
    pricingRegion,
    isPricingReady,
    formatPrice,
    localizeItemPrice,
    trackPricingEvent,
  } = usePricingRegion();
  const { addPackage, selectedPackage, setIsCartOpen } = useCart();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const copy = getPricingCopy(currentLang);
  const HeadingTag = headingLevel;

  useEffect(() => {
    const loadPackages = async () => {
      try {
        setPackages(await getPackages());
      } catch (error) {
        console.error('Error loading packages:', error);
        setPackages(createFallbackPackages());
      } finally {
        setLoading(false);
      }
    };

    loadPackages();
  }, []);

  const getPackageDetails = (pkg) => {
    const centralContent = getPackageContent(pkg.name, currentLang);
    return {
      name:
        centralContent?.name ||
        pkg.name_translations?.[currentLang] ||
        pkg.name,
      target:
        centralContent?.target ||
        pkg.description_translations?.[currentLang] ||
        pkg.description ||
        '',
      impacts:
        centralContent?.impacts?.length
          ? centralContent.impacts
          : (pkg.features || []).map(
              (feature) =>
                feature.text_translations?.[currentLang] ||
                feature.text ||
                feature
            ),
    };
  };

  const handleSelectPackage = (pkg) => {
    if (pkg.is_custom) {
      setShowCustomBuilder(true);
      return;
    }

    const pricedPackage = localizeItemPrice(pkg, 'PACKAGE');
    const details = getPackageDetails(pkg);
    addPackage({
      id: pkg.id,
      canonicalName: pkg.name,
      name: details.name,
      price: Number(pricedPackage.price || 0),
      basePriceUsd: Number(pkg.price || 0),
      pricingRegion: pricingRegion.id,
      currencyCode: pricingRegion.currencyCode,
      features: details.impacts,
      isCustom: false,
    });
    trackPricingEvent('package_selected', {
      itemKey: pkg.name,
      displayedAmount: Number(pricedPackage.price || 0),
    });
    setIsCartOpen(true);
  };

  const standardPackages = packages.filter((pkg) => !pkg.is_custom);
  const customPackage = packages.find((pkg) => pkg.is_custom);

  return (
    <>
      <section
        id="pricing"
        className="relative overflow-hidden bg-[#0a0a0a] py-24 md:py-32"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#00FFD1]/5 rounded-full blur-[150px] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
          <div className="text-center mb-16 md:mb-20">
            <HeadingTag className="text-4xl md:text-5xl lg:text-[48px] font-semibold text-white leading-[1.1] tracking-[-0.02em] mb-4">
              {copy.pricingTitle}
            </HeadingTag>
            <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto">
              {copy.pricingSubtitle}
            </p>
          </div>

          {loading || !isPricingReady ? (
            <PriceSkeleton cards={3} />
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-8">
                {standardPackages.map((pkg, index) => {
                  const details = getPackageDetails(pkg);
                  const pricedPackage = localizeItemPrice(pkg, 'PACKAGE');
                  const isSelected = selectedPackage?.id === pkg.id;

                  return (
                    <div
                      key={pkg.id || index}
                      data-testid={`package-card-${pkg.name?.toLowerCase()}`}
                      className={`relative group ${
                        pkg.is_popular
                          ? 'glass-card-featured scale-105 z-10'
                          : 'glass-card'
                      } ${isSelected ? 'ring-2 ring-[#00FFD1]' : ''}`}
                      style={{ borderRadius: '16px' }}
                    >
                      {pkg.is_popular && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 glass-badge flex items-center gap-1">
                          <Star size={14} fill="currentColor" />
                          {copy.popular}
                        </div>
                      )}

                      <div className="p-8 md:p-10 h-full flex flex-col">
                        <h3 className="text-xl font-semibold text-white/70 mb-3 tracking-wide">
                          {details.name}
                        </h3>
                        <p className="text-white/55 text-sm leading-relaxed min-h-[64px] mb-5">
                          {details.target}
                        </p>

                        <div className="mb-8">
                          <span className="text-white/45 text-sm block mb-1">
                            {copy.startingAt}
                          </span>
                          <div className="flex min-w-0 max-w-full flex-wrap items-end gap-x-2 gap-y-1">
                            <PackagePriceText
                              amount={pricedPackage.price}
                              formatPrice={formatPrice}
                              pricingRegion={pricingRegion}
                            />
                            <span className="text-white/40 text-sm pb-2">
                              {copy.oneTime}
                            </span>
                          </div>
                        </div>

                        <ul className="space-y-4 mb-8 flex-1">
                          {details.impacts.map((impact, impactIndex) => (
                            <li key={impactIndex} className="flex items-start gap-3">
                              <Check
                                size={18}
                                className="text-[#00FFD1] mt-1 flex-shrink-0"
                              />
                              <span className="text-white/80 text-base">
                                {impact}
                              </span>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handleSelectPackage(pkg)}
                          data-testid={`select-package-${pkg.name?.toLowerCase()}`}
                          className={`w-full ${
                            pkg.is_popular
                              ? 'btn-glass-primary'
                              : 'btn-glass-secondary'
                          }`}
                        >
                          {isSelected ? copy.selected : copy.selectPackage}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {customPackage && (() => {
                const details = getPackageDetails(customPackage);
                const pricedCustomPackage = localizeItemPrice(customPackage, 'PACKAGE');
                return (
                  <div
                    className="glass-card relative overflow-hidden"
                    data-testid="package-card-custom"
                    style={{
                      borderRadius: '16px',
                      background:
                        'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(18, 18, 18, 0.9) 100%)',
                      border: '1px solid rgba(0, 255, 209, 0.2)',
                    }}
                  >
                    <div className={`${isRTL ? 'left-6' : 'right-6'} absolute top-6`}>
                      <span
                        className="px-4 py-1.5 text-sm font-medium rounded-full"
                        style={{
                          background: 'rgba(0, 255, 209, 0.15)',
                          color: '#00FFD1',
                          border: '1px solid rgba(0, 255, 209, 0.3)',
                        }}
                      >
                        {copy.flexible}
                      </span>
                    </div>

                    <div className="p-8 md:p-10 md:flex md:items-center md:justify-between gap-8">
                      <div className="md:max-w-2xl">
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                          style={{
                            background: 'rgba(0, 255, 209, 0.15)',
                            border: '1px solid rgba(0, 255, 209, 0.3)',
                          }}
                        >
                          <Settings className="w-7 h-7 text-[#00FFD1]" />
                        </div>

                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                          {details.name}
                        </h3>
                        <p className="text-white/60 text-base mb-5">
                          {details.target}
                        </p>
                        <p className="text-white/50 text-base">
                          {copy.customDescription}
                        </p>
                      </div>

                      <div className="mt-8 md:mt-0 flex flex-col items-center md:items-end min-w-fit">
                        <div className="text-center md:text-end mb-4">
                          <span className="text-white/50 text-sm block mb-1">
                            {copy.startingAt}
                          </span>
                          <PackagePriceText
                            amount={pricedCustomPackage.price}
                            formatPrice={formatPrice}
                            pricingRegion={pricingRegion}
                            variant="compact"
                          />
                          <span className="text-white/40 text-sm">
                            {copy.oneTime}
                          </span>
                        </div>
                        <button
                          onClick={() => handleSelectPackage(customPackage)}
                          data-testid="build-custom-package-btn"
                          className="btn-glass-primary whitespace-nowrap"
                          style={{ padding: '14px 28px' }}
                        >
                          {copy.buildPackage}
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </section>

      {showCustomBuilder && (
        <CustomPackageBuilder
          customPackage={customPackage}
          onClose={() => setShowCustomBuilder(false)}
        />
      )}
    </>
  );
};

const CustomPackageBuilder = ({ customPackage, onClose }) => {
  const { currentLang, isRTL } = useLanguage();
  const {
    pricingRegion,
    formatPrice,
    localizeItemPrice,
    trackPricingEvent,
  } = usePricingRegion();
  const { addPackage, setIsCartOpen } = useCart();
  const [modulesByCategory, setModulesByCategory] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const copy = getPricingCopy(currentLang);

  useEffect(() => {
    const loadModules = async () => {
      try {
        setModulesByCategory(await getModulesByCategory());
      } catch (error) {
        console.error('Error loading custom package modules:', error);
        setModulesByCategory(createFallbackModuleGroups());
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, []);

  const getModuleDetails = (module) => {
    const centralContent = getModuleContent(module.name, currentLang);
    return {
      name:
        centralContent?.name ||
        module.name_translations?.[currentLang] ||
        module.name,
      impact:
        centralContent?.impact ||
        module.description_translations?.[currentLang] ||
        module.description ||
        '',
    };
  };

  const getCategoryName = (category) =>
    getCategoryContent(category.name, currentLang) ||
    category.name_translations?.[currentLang] ||
    category.name;

  const isExtraLanguage = (module) => module.name === 'Extra language';

  const getSelectedModule = (moduleId) =>
    selectedModules.find((module) => module.id === moduleId);

  const toggleModule = (module) => {
    setSelectedModules((current) => {
      const existing = current.find((item) => item.id === module.id);
      if (existing && isExtraLanguage(module)) {
        return current.map((item) =>
          item.id === module.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      if (existing) {
        return current.filter((item) => item.id !== module.id);
      }
      return [...current, { ...module, quantity: 1 }];
    });
  };

  const updateQuantity = (moduleId, nextQuantity) => {
    setSelectedModules((current) =>
      updateModuleQuantity(current, moduleId, nextQuantity)
    );
  };

  const pricedCustomPackage = localizeItemPrice(customPackage, 'PACKAGE');
  const basePrice = Number(pricedCustomPackage?.price || 0);
  const basePriceUsd = Number(customPackage?.price || 0);
  const selectedCount = selectedModules.reduce(
    (total, module) => total + (module.quantity || 1),
    0
  );
  const pricedSelectedModules = selectedModules.map((module) =>
    localizeItemPrice(module, 'MODULE')
  );
  const totalPrice = calculateCustomTotal(basePrice, pricedSelectedModules);

  const packageDetails =
    getPackageContent(customPackage?.name, currentLang) || {};

  const handleAddToCart = () => {
    const localizedModules = selectedModules.map((module) => {
      const pricedModule = localizeItemPrice(module, 'MODULE');
      return {
        id: module.id,
        canonicalName: module.name,
        name: getModuleDetails(module).name,
        price: Number(pricedModule.price || 0),
        basePriceUsd: Number(module.price || 0),
        quantity: module.quantity || 1,
        pricingRegion: pricingRegion.id,
        currencyCode: pricingRegion.currencyCode,
      };
    });

    addPackage({
      id: customPackage?.id || 'custom',
      canonicalName: customPackage?.name || 'CUSTOM',
      name:
        packageDetails.name ||
        customPackage?.name_translations?.[currentLang] ||
        customPackage?.name,
      price: totalPrice,
      isCustom: true,
      basePrice,
      basePriceUsd,
      pricingRegion: pricingRegion.id,
      currencyCode: pricingRegion.currencyCode,
      selectedModuleIds: expandModuleIds(localizedModules),
      modules: localizedModules,
    });
    trackPricingEvent('custom_package_selected', {
      itemKey: customPackage?.name || 'CUSTOM',
      displayedAmount: totalPrice,
      selectedModules: selectedCount,
    });
    setIsCartOpen(true);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto">
      <div
        className="min-h-screen py-4 md:py-8 px-3 md:px-4"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <div
          className="max-w-5xl mx-auto overflow-hidden"
          style={{
            background: 'rgba(18, 18, 18, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
          }}
        >
          <div className="p-5 md:p-8 border-b border-white/10 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                {copy.customTitle}
              </h2>
              <p className="text-white/50">{copy.chooseFeatures}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all flex-shrink-0"
              data-testid="close-custom-builder"
              aria-label={copy.remove}
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="p-5 md:p-8 min-w-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-[#00FFD1] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-8">
                  {modulesByCategory.map((group, index) => (
                    <div key={group.category?.id || index}>
                      <h3 className="text-lg font-semibold text-white/80 mb-4">
                        {getCategoryName(group.category)}
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {group.modules.map((module) => {
                          const selectedModule = getSelectedModule(module.id);
                          const details = getModuleDetails(module);
                          const quantity = selectedModule?.quantity || 0;
                          const pricedModule = localizeItemPrice(module, 'MODULE');

                          return (
                            <div
                              key={module.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => toggleModule(module)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  toggleModule(module);
                                }
                              }}
                              data-testid={`module-${module.id}`}
                              className={`p-4 rounded-xl text-start transition-all cursor-pointer min-w-0 ${
                                selectedModule
                                  ? 'bg-[#00FFD1]/15 border-[#00FFD1]'
                                  : 'bg-white/5 border-white/10 hover:bg-white/10'
                              }`}
                              style={{ border: '1px solid' }}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <span
                                    className={`font-medium block ${
                                      selectedModule ? 'text-white' : 'text-white/80'
                                    }`}
                                  >
                                    {details.name}
                                  </span>
                                  <span className="text-white/45 text-xs mt-1 block leading-relaxed">
                                    {details.impact}
                                  </span>
                                </div>
                                <span
                                  className={`price-value font-bold whitespace-nowrap ${
                                    selectedModule ? 'text-[#00FFD1]' : 'text-white/60'
                                  }`}
                                >
                                  {formatPrice(pricedModule.price)}
                                </span>
                              </div>

                              {selectedModule && isExtraLanguage(module) && (
                                <div
                                  className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <span className="text-white/50 text-xs">
                                    {copy.quantity}
                                  </span>
                                  <div className="quantity-control flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateQuantity(module.id, quantity - 1)
                                      }
                                      className="w-8 h-8 rounded-lg bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
                                      aria-label={copy.remove}
                                    >
                                      <Minus size={15} />
                                    </button>
                                    <span className="text-white font-bold min-w-6 text-center">
                                      {quantity}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateQuantity(module.id, quantity + 1)
                                      }
                                      className="w-8 h-8 rounded-lg bg-[#00FFD1] text-black flex items-center justify-center hover:bg-[#00FFD1]/80"
                                      aria-label={copy.quantity}
                                    >
                                      <Plus size={15} />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <aside className="border-t lg:border-t-0 lg:border-s border-white/10 p-5 md:p-6 bg-[#00FFD1]/[0.03] min-w-0">
              <div className="lg:sticky lg:top-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 min-w-0">
                  <h3 className="text-white font-semibold">
                    {copy.selectedModules}
                  </h3>
                  <span className="glass-badge text-xs whitespace-normal text-center break-words max-w-full">
                    {copy.selectedCount}: {selectedCount}
                  </span>
                </div>

                {selectedModules.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/15 p-5 text-white/45 text-sm leading-relaxed">
                    {copy.chooseFeatures}
                  </div>
                ) : (
                  <div className="space-y-2 mb-6">
                    {selectedModules.map((module) => {
                      const details = getModuleDetails(module);
                      const quantity = module.quantity || 1;
                      const pricedModule = localizeItemPrice(module, 'MODULE');
                      return (
                        <div
                          key={module.id}
                          className="rounded-xl bg-white/5 border border-white/10 p-3 min-w-0"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <span className="text-white/80 text-sm break-words">
                                {details.name}
                              </span>
                              {quantity > 1 && (
                                <span className="text-white/45 text-xs block mt-1">
                                  {copy.quantity}: {quantity}
                                </span>
                              )}
                            </div>
                            <div className="text-end flex-shrink-0">
                              <span className="price-value text-[#00FFD1] text-sm font-semibold block">
                                {formatPrice(Number(pricedModule.price || 0) * quantity)}
                              </span>
                              <button
                                type="button"
                                onClick={() => updateQuantity(module.id, 0)}
                                className="text-red-300/70 hover:text-red-300 text-xs mt-1"
                              >
                                {copy.remove}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="border-t border-white/10 pt-5 space-y-3">
                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-white/50">{copy.basePrice}</span>
                    <span className="price-value text-white/80 font-medium">
                      {formatPrice(basePrice)}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-white/50 text-sm">
                      {copy.estimatedTotal}
                    </span>
                    <span className="price-value text-[#00FFD1] text-3xl font-bold break-words">
                      {formatPrice(totalPrice)}
                    </span>
                    <span className="text-white/40 text-xs">{copy.oneTime}</span>
                  </div>
                  <p className="text-white/40 text-xs leading-relaxed">
                    {copy.estimateNotice}
                  </p>
                  <button
                    onClick={handleAddToCart}
                    disabled={selectedModules.length === 0}
                    data-testid="add-custom-to-cart"
                    className="w-full btn-glass-primary disabled:opacity-50 disabled:cursor-not-allowed justify-center"
                    style={{ padding: '14px 20px' }}
                  >
                    {copy.addSelection}
                    <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
